import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  AnalyticsService,
  InfoItem,
  PaginationComponent,
  ResponsiveService,
} from '@frk/eds-components';
import { NAV } from '@models';
import { PricingHistory } from '@products/models/pricing-history';
import { EMDASH } from '@products/utils/constants/product.constants';
import { BaseTableResponsiveComponent } from '@shared/table/base-table-responsive.component';
import { TranslateService } from '@shared/translate/translate.service';
import {
  CurrencyCode,
  DropdownOption,
  EDSPaginationPageChangedEvent,
  FundId,
  InfoItemWithKey,
  PricingRangeUpdateValues,
  PricingState,
  PricingTableHistoricalSummary,
  ToggleOptionWithPeriod,
} from '@types';
import {
  ANALYTICS_DATE_EVENT,
  ANALYTICS_DATE_FORMAT,
  ANALYTICS_DETAILED_DATE_EVENT,
} from '@utils/app.constants';
import { Logger } from '@utils/logger';
import { SiteConfigService } from '@services';
import { generateUUID } from '@utils/text/string-utils';
import dayjs, { Dayjs } from 'dayjs';
import { Observable } from 'rxjs';

const logger = Logger.getLogger('PricingHistoricalComponent');

@Component({
  selector: 'ft-pricing-historical',
  templateUrl: './pricing-historical.component.html',
  styleUrls: ['./pricing-historical.component.scss'],
})
export class PricingHistoricalComponent
  extends BaseTableResponsiveComponent
  implements OnInit, OnChanges {
  @Input() pricingState: PricingState;
  @Input() filteredHistoricalList: PricingHistory[];
  @Input() historicalTableTotalPages: {
    count: number;
  };
  @Input() fundId: FundId;
  @Input() isdatePickerAnalyticsEnabled = true; // For Disabling Analytics for Date Selection
  @Output()
  toggleTableRangeUpdated = new EventEmitter<PricingRangeUpdateValues>();
  @Output() selectedPage = new EventEmitter<number>();
  @Output() downloadAction = new EventEmitter();
  infoItemsWithKey: InfoItemWithKey[] = [];
  dropdownRange: DropdownOption[] = [];
  selectedDropdownRange: string;
  incDateStd: string;
  asOfDateStd: string;
  fromDateStd: string;
  toDateStd: string;
  fromDate: string;
  toDate: string;
  currencyCode: CurrencyCode;
  isSecondaryCalcTypeAvailable: boolean;
  historicalList: PricingHistory[] = [];
  totalPages: number;
  showPop: boolean;
  showMarketPriceData: boolean;
  isSiteIntl: boolean;
  showMoneyMarketPriceData: boolean;
  showApplicationPrice: boolean;
  showRedemptionPrice: boolean;
  showForETF: boolean;
  showMoneyMarketNetFlowColumn: boolean;
  showMoneyMarketMasterNetFlowColumn: boolean;
  showIntradayNavData: boolean;

  // DCE-2749 Site Config Date Format
  public siteConfigDateFormat: string;

  public showMasterFundNav: boolean;
  public showDailyLiquidAssetRatio: boolean;
  public showWeeklyLiquidAssetRatio: boolean;
  public showNetShareHolderFlow: boolean;
  public dropdownId = `priceHistoricalDropDown${generateUUID()}`;
  pageTitle: string;
  public get getDropdownId(): string {
    return this.dropdownId;
  }

  @ViewChild(PaginationComponent, { static: false })
  paginationComponent: PaginationComponent;
  @ViewChild('scroll') scrollPoint: ElementRef;
  isMobile$: Observable<boolean>;

  constructor(
    protected translateService: TranslateService,
    protected responsiveService: ResponsiveService,
    private analytics: AnalyticsService,
    private titleService: Title,
    private siteConfig: SiteConfigService
  ) {
    super(responsiveService);
    this.isMobile$ = this.responsiveService.isMobile$();
  }

  ngOnInit(): void {
    super.ngOnInit();
    // DCE-2749 Get date format required from siteConfig
    this.siteConfigDateFormat = this.siteConfig
      ?.getGlobalConfig()
      ?.siteConfiguration?.site?.dateFormat?.toUpperCase();
    this.populatePricingState(this.pricingState);
    this.pageTitle = this.titleService.getTitle();
    this.historicalList = this.filteredHistoricalList;
    this.totalPages = this.historicalTableTotalPages.count;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pricingState && !changes.pricingState.firstChange) {
      //  avoid duplicate trigger of populate method on first load.
      this.populatePricingState(changes.pricingState.currentValue);
    }

    if (
      changes.filteredHistoricalList &&
      !changes.filteredHistoricalList.firstChange
    ) {
      this.historicalList = changes.filteredHistoricalList.currentValue;
    }

    if (
      changes.historicalTableTotalPages &&
      !changes.historicalTableTotalPages.firstChange
    ) {
      this.totalPages = changes.historicalTableTotalPages.currentValue.count;
      this.paginationComponent?.updatePagination(this.totalPages); // Perform changes on pagination component.
    }
  }

  /**
   * Initializes properties on page load.
   * Again, called when 'input' changes.
   */
  populatePricingState(pricingState: PricingState): void {
    this.selectedDropdownRange = pricingState.tableConfig.selectedToggleRange;
    this.incDateStd = pricingState.pricingConfig.dateValues.incDateStd;
    this.asOfDateStd = pricingState.pricingConfig.dateValues.asOfDateStd;
    this.fromDateStd = pricingState.tableConfig.customRange.fromDateStd;
    this.toDateStd = pricingState.tableConfig.customRange.toDateStd;
    this.fromDate = pricingState.tableConfig.customRange.fromDate;
    this.toDate = pricingState.tableConfig.customRange.toDate;
    this.currencyCode = pricingState.historicalData[0]?.currencyCode;
    this.isSecondaryCalcTypeAvailable =
      pricingState.pricingConfig.isSecondaryCalcTypeAvailable;
    this.dropdownRange = this.constructPriceRangeDropdownOptions(pricingState);
    this.infoItemsWithKey = this.constructInfoItems(
      pricingState.overviewData.nav,
      pricingState.tableConfig
    );
    this.showPop = pricingState.pricingConfig.showPop;
    this.showMarketPriceData = pricingState.pricingConfig.showMarketPriceData;
    this.isSiteIntl = pricingState.isSiteIntl;
    this.showMoneyMarketPriceData =
      pricingState.pricingConfig.showMoneyMarketPriceData;
    // checking the variables for VIP table to show following fields
    this.showMasterFundNav = pricingState.pricingConfig.showMasterFundNav;
    this.showDailyLiquidAssetRatio =
      pricingState.pricingConfig.showDailyLiquidAssetRatio;
    this.showWeeklyLiquidAssetRatio =
      pricingState.pricingConfig.showWeeklyLiquidAssetRatio;
    this.showNetShareHolderFlow =
      pricingState.pricingConfig.showNetShareHolderFlow;

    this.showApplicationPrice = pricingState.pricingConfig.showApplicationPrice;
    this.showRedemptionPrice = pricingState.pricingConfig.showRedemptionPrice;
    this.showForETF = pricingState.pricingConfig.showForETF;
    this.showMoneyMarketNetFlowColumn =
      pricingState.pricingConfig.showMoneyMarketNetFlowColumn;
    this.showMoneyMarketMasterNetFlowColumn =
      pricingState.pricingConfig.showMoneyMarketMasterNetFlowColumn;
    this.showIntradayNavData =
      this.pricingState?.overviewData?.nav?.intradayNav !== '' &&
      !pricingState.pricingConfig.hideIntraDayNav;
  }

  /**
   * Construct price range toggle options with static values.
   * Toggle active will be set as per 'pricingSelectedToggleRange'.
   */
  constructPriceRangeDropdownOptions(
    pricingState: PricingState
  ): DropdownOption[] {
    return Array.from(pricingState.tableConfig.dateRangeEnum.values())
      .filter(
        (dropdownOption: ToggleOptionWithPeriod) =>
          dropdownOption.value !== 'CUSTOM_RANGE'
      )
      .map((dropdownOption: ToggleOptionWithPeriod) => ({
        value: dropdownOption.value,
        label: this.translateService.instant(dropdownOption.text),
        link: '#',
      }));
  }

  /**
   * Construct info items array to be displayed on summary panel.
   * @param nav nav data
   * @param historicalSummary historical summary for particular date range
   */
  constructInfoItems(
    nav: NAV,
    historicalSummary: PricingTableHistoricalSummary
  ): InfoItemWithKey[] {
    return [
      {
        key: 'APPLICATION_PRICE',
        label: this.translateService.instant(
          'pds.APPLICATION_PRICE',
          this.fundId
        ),
        val: `${nav.applicationPrice} (${nav.currencyCode})`,
        asOfLabel: this.translateService.instant('products.as-of'),
        asOfDate: nav.navAsOfDate,
      },
      {
        key: 'REDEMPTION_PRICE',
        label: this.translateService.instant(
          'pds.REDEMPTION_PRICE',
          this.fundId
        ),
        val: `${nav.redemptionPrice} (${nav.currencyCode})`,
        asOfLabel: this.translateService.instant('products.as-of'),
        asOfDate: nav.navAsOfDate,
      },
      {
        key: 'NAV',
        label: this.translateService.instant('pds.NAV_VALUE', this.fundId),
        val: `${nav.navValue} (${nav.currencyCode})`,
        asOfLabel: this.translateService.instant('products.as-of'),
        asOfDate: nav.navAsOfDate,
      },
      {
        key: 'HIGHEST_NAV',
        label: this.translateService.instant(
          'pds.HIGH_NAV_VALUE_YTD',
          this.fundId
        ),
        val: `${historicalSummary.highestNav} (${nav.currencyCode})`,
        asOfLabel: this.translateService.instant('products.as-at'),
        asOfDate: historicalSummary.highestNavDate,
      },
      {
        key: 'LOWEST_NAV',
        label: this.translateService.instant(
          'pds.LOW_NAV_VALUE_YTD',
          this.fundId
        ),
        val: `${historicalSummary.lowestNav} (${nav.currencyCode})`,
        asOfLabel: this.translateService.instant('products.as-at'),
        asOfDate: historicalSummary.lowestNavDate,
      },
      {
        key: 'MARKET_PRICE',
        label: this.translateService.instant(
          'pds.MARKET_PRICE_VALUE',
          this.fundId
        ),
        val: nav.marketPriceValue
          ? `${nav.marketPriceValue} (${nav.currencyCode})`
          : EMDASH,
        asOfLabel: this.translateService.instant('products.as-of'),
        asOfDate: nav.marketPriceAsOfDate,
      },
      {
        key: 'HIGHEST_MARKET_PRICE',
        label: this.translateService.instant(
          'pds.HIGH_MARKET_PRICE_VALUE_YTD',
          this.fundId
        ),
        val: historicalSummary.highestMarketPrice
          ? `${historicalSummary.highestMarketPrice} (${nav.currencyCode})`
          : EMDASH,
        asOfLabel: this.translateService.instant('products.as-at'),
        asOfDate: historicalSummary.highestMarketPriceDate,
      },
      {
        key: 'LOWEST_MARKET_PRICE',
        label: this.translateService.instant(
          'pds.LOW_MARKET_PRICE_VALUE_YTD',
          this.fundId
        ),
        val: historicalSummary.lowestMarketPrice
          ? `${historicalSummary.lowestMarketPrice} (${nav.currencyCode})`
          : EMDASH,
        asOfLabel: this.translateService.instant('products.as-at'),
        asOfDate: historicalSummary.lowestMarketPriceDate,
      },
      {
        key: 'POP',
        label: this.translateService.instant('products.POP_VALUE', this.fundId),
        val: `${nav.popValue} (${nav.currencyCode})`,
        asOfLabel: this.translateService.instant('products.as-of'),
        asOfDate: nav.navAsOfDate,
      },
      {
        key: 'MASTER_FUND_NAV',
        label: this.translateService.instant(
          'products.Master-Fund-Level-Market-Nav',
          this.fundId
        ),
        val: `${nav.masterFundNav} (${nav.currencyCode})`,
        asOfLabel: this.translateService.instant('products.as-of'),
        asOfDate: nav.navAsOfDate,
      },
      {
        key: 'INTRADAY_NAV',
        label: this.translateService.instant(
          'products.INTRADAY_NAV',
          this.fundId
        ),
        val: `${nav.intradayNav} (${nav.currencyCode})`,
        asOfLabel: this.translateService.instant('products.as-of'),
        asOfDate: nav.intradayNavDate,
      },
      {
        key: 'Fund-Level-Market-Nav',
        label: this.translateService.instant(
          'products.Fund-Level-Market-Nav',
          this.fundId
        ),
        val: `${nav.moneyFundMarketNav} (${nav.currencyCode})`,
        asOfLabel: this.translateService.instant('products.as-of'),
        asOfDate: nav.navAsOfDate,
      },
    ];
  }

  /**
   * Fetch info item object by key name
   * @param key info item key
   */
  getInfoItem(key: string): InfoItem {
    const infoItem = this.infoItemsWithKey.find(
      (infoItemWithKey: InfoItemWithKey) => infoItemWithKey.key === key
    );

    return {
      label: infoItem.label,
      val: infoItem.val,
      asOfLabel: infoItem.asOfLabel,
      asOfDate: infoItem.asOfDate,
    };
  }

  /**
   * Set price range as per toggle value selected.
   * @param selectedDropdownRange toggle value selected
   */
  setDropdownDateRange(selectedDropdownOption: DropdownOption): void {
    this.toggleTableRangeUpdated.next({
      selectedToggleRange: selectedDropdownOption.value,
    });
  }

  getDropdownTitleText(selectedValue?: string) {
    const selectedDropdown = this.dropdownRange.filter(
      (dropdownOption: DropdownOption) => {
        return dropdownOption.value === selectedValue;
      }
    );
    return selectedDropdown?.length > 0 ? selectedDropdown[0].label : '';
  }

  /**
   * Set price range as per calendar custom value selected.
   * @param inputType the input selected i.e., fromDate/toDate
   * @param event onclick event generated
   */
  setCustomDateRange(inputType: string, date: Dayjs): void {
    logger.debug('date:::::::', date);
    if (dayjs.isDayjs(date)) {
      const selectedDate = date.format();

      if (inputType === 'fromDate') {
        this.toggleTableRangeUpdated.next({
          selectedToggleRange: 'CUSTOM_RANGE',
          fromDate: selectedDate,
          toDate: this.toDateStd,
        });
      } else {
        this.toggleTableRangeUpdated.next({
          selectedToggleRange: 'CUSTOM_RANGE',
          fromDate: this.fromDateStd,
          toDate: selectedDate,
        });
      }

      if (this.isdatePickerAnalyticsEnabled) {
        this.analytics.trackEvent({
          event: ANALYTICS_DATE_EVENT,
          detailed_event: ANALYTICS_DETAILED_DATE_EVENT,
          action: 'Date Range',
          label: `${dayjs(this.fromDateStd).format(
            ANALYTICS_DATE_FORMAT
          )} - ${dayjs(this.toDateStd).format(ANALYTICS_DATE_FORMAT)}`,
          event_data: {
            date_type: ANALYTICS_DATE_FORMAT,
            date_value: `${dayjs(this.fromDateStd).format(
              ANALYTICS_DATE_FORMAT
            )} - ${dayjs(this.toDateStd).format(ANALYTICS_DATE_FORMAT)}`,
          },
        });
      }
    }
  }

  /**
   * Trigger page change with the page number selected.
   * @param event page changed event generated
   */
  onPageChanged(event: EDSPaginationPageChangedEvent): void {
    if (event.action !== 'load') {
      this.selectedPage.next(event.page);
      // Scroll the view, when listing is changed.
      if (this.scrollPoint) {
        const element = this.scrollPoint.nativeElement;
        // TODO: refactor scrolling to use WindowScrollService
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  /**
   * Trigger download action.
   */
  onDownloadButtonClicked(): void {
    this.analytics.trackEvent({
      location: '',
      event: 'file_download',
      detailed_event: 'Download Link Clicked',
      file_name: this.pricingState.overviewData.fundName,
      file_extension: 'xlsx',
      event_data: {
        file_extension: 'xlsx',
        file_lit_code: '',
        file_name: this.pricingState.overviewData.fundName,
        link_url: '',
        location: '',
      },
    });
    this.downloadAction.next();
  }
}
